'use client';

import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Settings, Book, Loader2, Home } from 'lucide-react';
import GoodMarkdownParser from './safeMarkedParser';
import Link from 'next/link';

type Book = {
    id: string;
    title: string;
    category?: string;
    content_type?: string;
    created_at?: string | null;
};

interface ScriptureReaderProps {
    sections: any[];
    description?: string;
    books: Book[];
    selectedBookId: string | null;
    onBookChange: (id: string) => void;
    onClose: () => void;
    loading?: boolean;
}

const TESTAMENT_ORDER = ["Guides", 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth'];

export const ScriptureReader: React.FC<ScriptureReaderProps> = ({
    sections,
    description,
    books,
    selectedBookId,
    onBookChange,
    onClose,
    loading
}) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [chapterStartPages, setChapterStartPages] = useState<number[]>([]);
    const [isMeasuring, setIsMeasuring] = useState(true);

    const [fontSize, setFontSize] = useState(18);
    const [fontFamily, setFontFamily] = useState<'serif' | 'sans'>('serif');
    const [showSettings, setShowSettings] = useState(false);

    const contentRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const proseRef = useRef<HTMLDivElement>(null);

    const COLUMN_GAP = 80;

    // Inside ScriptureReader component, before return
    const canonicalize = (raw?: string) => {
        if (!raw) return 'Other';
        const s = raw.toLowerCase();
        if (s.includes('guide')) return 'Guides';
        if (s.includes('first')) return 'First';
        if (s.includes('second')) return 'Second';
        if (s.includes('third')) return 'Third';
        if (s.includes('fourth')) return 'Fourth';
        if (s.includes('fifth')) return 'Fifth';
        if (s.includes('sixth')) return 'Sixth';
        if (s.includes('seventh')) return 'Seventh';
        if (s.includes('eighth')) return 'Eighth';
        return raw;
    };

  const groupedBooks = useMemo(() => {
        const acc = books.reduce((map, book) => {
            const key = canonicalize(book.content_type || book.category);
            if (!map[key]) map[key] = [] as Book[];
            map[key].push(book);
            return map;
        }, {} as Record<string, Book[]>);

        Object.keys(acc).forEach((k) => {
            acc[k].sort((a, b) => {
                const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
                const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
                return ta - tb;
            });
        });

        return acc;
    }, [books]);

    const displaySections = useMemo(() => {
        if (description) {
            const prologue = {
                id: 'intro',
                content: description,
                title: 'Introduction',
            };
            return [prologue, ...sections];
        }
        return sections;
    }, [sections, description]);

    const measurePages = useCallback(() => {
        if (contentRef.current && containerRef.current && proseRef.current) {
            const scrollWidth = contentRef.current.scrollWidth;
            const pageWidth = containerRef.current.clientWidth;
            const effectivePageStride = pageWidth + COLUMN_GAP;

            const pages = Math.max(1, Math.ceil((scrollWidth + COLUMN_GAP) / effectivePageStride));
            setTotalPages(pages);

            const chapterWrappers = Array.from(proseRef.current.children).filter(
                child => child.classList.contains('chapter-wrapper')
            ) as HTMLElement[];

            const starts: number[] = [];
            chapterWrappers.forEach((wrapper) => {
                let offset = wrapper.offsetLeft;
                const pageIndex = Math.round(offset / effectivePageStride);
                starts.push(pageIndex);
            });

            if (starts.length > 0) setChapterStartPages(starts);
            setIsMeasuring(false);
        }
    }, [COLUMN_GAP]);

    useLayoutEffect(() => {
        setIsMeasuring(true);
        const timer = setTimeout(() => measurePages(), 200);
        const handleResize = () => { setIsMeasuring(true); measurePages(); };
        window.addEventListener('resize', handleResize);
        return () => { window.removeEventListener('resize', handleResize); clearTimeout(timer); };
    }, [fontSize, measurePages, displaySections, fontFamily]);

    // Reset page to 0 when book changes
    useEffect(() => {
        setCurrentPage(0);
    }, [selectedBookId]);

    const goToNextPage = () => { if (currentPage < totalPages - 1) setCurrentPage(prev => prev + 1); };
    const goToPrevPage = () => { if (currentPage > 0) setCurrentPage(prev => prev - 1); };

    let activeChapterIndex = 0;
    for (let i = 0; i < chapterStartPages.length; i++) {
        if (currentPage >= chapterStartPages[i]) activeChapterIndex = i;
        else break;
    }

    const currentSection = displaySections[activeChapterIndex] || displaySections[0];
    const progressPercentage = ((currentPage + 1) / totalPages) * 100;

    const jumpToChapter = (chapterIndex: number) => {
        const targetPage = chapterStartPages[chapterIndex];
        if (targetPage !== undefined) {
            setCurrentPage(targetPage);
        }
    };



    return (
        <div className="fixed inset-0 z-50 bg-[#FDFBF7] flex flex-col text-slate-800 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white/50 backdrop-blur-sm relative z-20 shadow-sm h-14 shrink-0">
                <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center gap-2 bg-stone-100 px-3 py-1.5 rounded-lg border border-stone-200">
                        <Book size={16} className="text-amber-700" />
                        <select
                            value={selectedBookId || ''}
                            onChange={(e) => onBookChange(e.target.value)}
                            className="bg-transparent text-sm font-bold focus:outline-none cursor-pointer w-full"
                        >
                            {TESTAMENT_ORDER.map(testament => {
                                const categoryBooks = groupedBooks[testament];

                                if (!categoryBooks || categoryBooks.length === 0) return null;

                                const label = testament === 'Guides' ? 'Guides' : `${testament} Testament`;

                                return (
                                    <optgroup key={testament} label={label} className="bg-stone-100 text-stone-500 font-semibold italic">
                                        {categoryBooks.map(book => (
                                            <option key={book.id} value={book.id} className="text-stone-900 font-bold not-italic">
                                                {book.title}
                                            </option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                        </select>
                    </div>

                    {loading && <Loader2 className="animate-spin text-stone-400" size={18} />}

                    {!loading && (
                        <div className="hidden sm:flex items-center gap-2">
                            <div className="h-4 w-px bg-stone-300 mx-2"></div>
                            <select
                                value={activeChapterIndex}
                                onChange={(e) => jumpToChapter(parseInt(e.target.value))}
                                className="bg-transparent text-sm font-semibold text-stone-700 focus:outline-none cursor-pointer max-w-[200px] hover:text-amber-700 transition-colors"
                            >
                                {displaySections.map((section, idx) => (
                                    <option key={section.id} value={idx}>
                                        {section.title || `Chapter ${idx + 1}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <Link
                    href="/"

                        className={`p-2 rounded-full transition-colors ${false ? 'bg-amber-100 text-amber-700' : 'hover:bg-stone-200 text-stone-500'}`}
                    >
                        <Home size={20} />
                    </Link>
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-2 rounded-full transition-colors ${showSettings ? 'bg-amber-100 text-amber-700' : 'hover:bg-stone-200 text-stone-500'}`}
                    >
                        <Settings size={20} />
                    </button>
                </div>
            </div>

            {/* Settings Dropdown */}
            {showSettings && (
                <div className="absolute top-14 right-4 z-30 bg-white p-4 rounded-xl shadow-xl border border-stone-200 w-64 space-y-4">
                    <div>
                        <label className="text-xs font-bold text-gray-400 uppercase block mb-2">Font Size</label>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setFontSize(prev => Math.max(14, prev - 2))} className="p-2 bg-gray-100 rounded flex-1">-</button>
                            <span className="w-12 text-center font-mono">{fontSize}px</span>
                            <button onClick={() => setFontSize(prev => Math.min(32, prev + 2))} className="p-2 bg-gray-100 rounded flex-1">+</button>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setFontFamily('serif')} className={`flex-1 py-2 rounded ${fontFamily === 'serif' ? 'bg-stone-800 text-white' : 'bg-stone-100'}`}>Serif</button>
                        <button onClick={() => setFontFamily('sans')} className={`flex-1 py-2 rounded ${fontFamily === 'sans' ? 'bg-stone-800 text-white' : 'bg-stone-100'}`}>Sans</button>
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="flex-1 relative overflow-hidden w-full max-w-4xl mx-auto">
                <div className="absolute inset-y-0 left-0 w-16 z-10 cursor-w-resize hover:bg-gradient-to-r hover:from-black/5 hover:to-transparent transition-all flex items-center justify-start group" onClick={goToPrevPage}>
                    <ChevronLeft className="ml-2 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                </div>
                <div className="absolute inset-y-0 right-0 w-16 z-10 cursor-e-resize hover:bg-gradient-to-l hover:from-black/5 hover:to-transparent transition-all flex items-center justify-end group" onClick={goToNextPage}>
                    <ChevronRight className="mr-2 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                </div>
                <div
                    className="h-full w-full p-8 md:p-16 box-border transition-opacity duration-300"
                    style={{ opacity: (isMeasuring || loading) ? 0 : 1 }}
                >
                    <div ref={containerRef} className="h-full w-full overflow-hidden">
                        <div
                            ref={contentRef}
                            className={`h-full w-full ${fontFamily === 'serif' ? 'font-serif' : 'font-sans'}`}
                            style={{
                                columnCount: 1,
                                columnGap: `${COLUMN_GAP}px`,
                                fontSize: `${fontSize}px`,
                                lineHeight: 1.8,
                                transform: `translateX(calc(-${currentPage} * (100% + ${COLUMN_GAP}px)))`,
                                transition: 'transform 0.5s cubic-bezier(0.2, 0, 0, 1)',
                            }}
                        >
                            <div ref={proseRef} className="prose prose-slate max-w-none">
                                {displaySections.map((section, idx) => (
                                    <div
                                        key={section.id}
                                        className="chapter-wrapper"
                                        style={{
                                            breakBefore: idx === 0 ? 'auto' : 'column',
                                            paddingBottom: '4rem'
                                        }}
                                    >
                                        <h1 className="text-4xl font-bold text-center mb-12 text-slate-900">
                                            {section.title || `Chapter ${idx + 1}`}
                                        </h1>
                                        <GoodMarkdownParser content={section.content} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="h-12 bg-white border-t border-stone-200 flex items-center justify-between px-6 text-xs text-stone-500 font-medium">
                <div className="w-48">
                    <span className="font-bold text-amber-700">{books.find(b => b.id === selectedBookId)?.title}</span>
                </div>
                <div className="flex-1 max-w-md mx-4 h-1 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-600 transition-all" style={{ width: `${progressPercentage}%` }} />
                </div>
                <div className="w-48 text-right">
                    Page {currentPage + 1} of {totalPages}
                </div>
            </div>
        </div>
    );
};